// @flow
import { find, flatten, get } from "lodash";
import type { BettingInterest, Runner } from "@tvg/types/Race";
import { WagerTypes } from "@fdr/types/WagerTypes";
import type { WagerTypesSelected } from "@fdr/atomic-ui/_templates/BetConfirmation";
import type { NullaryFn } from "@tvg/types/Functional";
import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";

export const BetStatus = {
  processing: "PROCESSING",
  error: "ERROR",
  success: "PLACED"
};

export const BetError = {
  raceOff: 136505,
  insufficientFunds: 136518,
  wagerLimits: 136521,
  maxWagerSizeLimit: 136522,
  expiredToken: 158405,
  stateMismatch: 136525,
  geoComplyTokenRejected: 136524,
  geoComplyTokenMissing: 136463
};

export type BetGroupDetails = {
  [key: number]: string[],
  allSelections: Array<Array<number>>
};

export type BetDetailsParams = {
  betType: WagerTypesSelected,
  selections: string[][] | BetGroupDetails,
  bettingInterests: BettingInterest[][],
  betAmount: number,
  betCount: number
};

export const getBetSelectionsToShow = (
  betSelections: string[][],
  bettingInterests: BettingInterest[][] = [],
  isHorseName: boolean = false
): string[][] => {
  // if we don't have betting interest info return the selections values
  if (bettingInterests.length === 0) {
    return betSelections.map((column) =>
      column.map((selection) => `${selection}`)
    );
  }

  const betSelectionsToShow = [];
  // leg races have multiple betInterest
  const isLegRace = bettingInterests.length > 1;

  betSelections.forEach((selection: string[], index: number) => {
    const column = [];
    // if race is leg we should get the correct one

    const raceBettingInterests = isLegRace
      ? bettingInterests[index]
      : bettingInterests[0];

    selection.forEach((bet: string) => {
      const betInterest = find(
        raceBettingInterests,
        (bi) => bet === bi.biNumber.toString()
      );

      get(betInterest, "runners", []).forEach((runner: Runner) => {
        if (!get(runner, "scratched")) {
          column.push(
            isHorseName
              ? get(runner, "horseName")
              : get(runner, "runnerId").toString()
          );
        }
      });
    });
    betSelectionsToShow.push(column);
  });
  return betSelectionsToShow;
};

export const findWagerTypeByGroup = (
  wagerTypeId: string,
  searchWagerId: string
) => wagerTypeId === searchWagerId;

export const extractArray = (
  arrayToRemove: string[],
  valueToExtract: string
): string[] =>
  arrayToRemove.filter((value) => !findWagerTypeByGroup(value, valueToExtract));

export const getBetSelectionSpecialGroup = (
  betSelections: string[][],
  wagerCode: string
): BetGroupDetails => {
  let win = [...get(betSelections, 0, [])];
  let place =
    wagerCode === WagerTypeCodesEnum.WIN_SHOW
      ? []
      : [...get(betSelections, 1, [])];
  let show = [...get(betSelections, 2, [])];
  const winPlace = [];
  const winShow = [];
  const placeShow = [];
  const winPlaceShow = [];
  const allSelections = [[...win, ...place, ...show]];

  win = win.reduce((winWagerList, winWagerType) => {
    if (isNaN(winWagerType)) {
      return winWagerList;
    }

    const placeWagerType = place.find((wagerType) =>
      findWagerTypeByGroup(wagerType, winWagerType)
    );
    const showWagerType = show.find((wagerType) =>
      findWagerTypeByGroup(wagerType, winWagerType)
    );

    if (placeWagerType && showWagerType) {
      winPlaceShow.push(winWagerType);
      place = extractArray(place, winWagerType);
      show = extractArray(show, winWagerType);
    } else if (placeWagerType) {
      winPlace.push(placeWagerType);
      place = extractArray(place, winWagerType);
    } else if (showWagerType) {
      winShow.push(showWagerType);
      show = extractArray(show, winWagerType);
    } else {
      winWagerList.push(winWagerType);
    }

    return winWagerList;
  }, []);

  place = place.reduce((placeWagerList, placeWagerType) => {
    if (isNaN(placeWagerType)) {
      return placeWagerList;
    }

    const showWagerType = show.find((wagerType) =>
      findWagerTypeByGroup(wagerType, placeWagerType)
    );

    if (showWagerType) {
      placeShow.push(showWagerType);
      show = extractArray(show, showWagerType);
    } else {
      placeWagerList.push(placeWagerType);
    }

    return placeWagerList;
  }, []);

  return {
    [WagerTypes.Win]: win,
    [WagerTypes.Place]: place,
    [WagerTypes.Show]: show,
    [WagerTypes.WinPlace]: winPlace,
    [WagerTypes.WinShow]: winShow,
    [WagerTypes.PlaceShow]: placeShow,
    [WagerTypes.WinPlaceShow]: winPlaceShow,
    allSelections
  };
};

export const getMultiplierSpecialGroup = (wagerId: number) => {
  let multiplier = 0;

  if (wagerId === WagerTypes.WinPlaceShow) {
    multiplier += 3;
  } else if (
    wagerId === WagerTypes.WinPlace ||
    wagerId === WagerTypes.WinShow ||
    wagerId === WagerTypes.PlaceShow
  ) {
    multiplier += 2;
  } else {
    multiplier += 1;
  }

  return multiplier;
};

export const getBetDetails = ({
  betType,
  selections,
  bettingInterests = [],
  betAmount,
  betCount
}: BetDetailsParams) => {
  const isSpecialGroup = get(betType, "specialGroup", false);
  const wagerTypes = isSpecialGroup ? get(betType, "types") : [betType];

  return wagerTypes.reduce(
    (wagerTypeList: WagerTypesSelected[], wagerType: any) => {
      let betSelections = [];
      let allSelections;

      const betTypeId = get(wagerType, "type.id");

      if (isSpecialGroup) {
        betSelections = [get(selections, betTypeId, [])];
        allSelections = flatten(betSelections).length;
      } else {
        betSelections = (selections: any);
        allSelections = get(selections, 0, []).length;
      }

      const selectionsFiltered = getBetSelectionsToShow(
        (betSelections: string[][]),
        bettingInterests
      );

      const horseNameFiltered = getBetSelectionsToShow(
        (betSelections: string[][]),
        bettingInterests,
        true
      );

      if (allSelections > 0) {
        let betTotal = betAmount * betCount;

        if (isSpecialGroup) {
          betTotal =
            betAmount * getMultiplierSpecialGroup(betTypeId) * allSelections;
        }

        wagerTypeList.push({
          wagerType,
          betSelections,
          selections: selectionsFiltered,
          betTotal,
          wagerAmount: betAmount,
          horseNamesList: horseNameFiltered[0]
        });
      }

      return wagerTypeList;
    },
    []
  );
};

export const asyncSubmit = async (
  array: NullaryFn<any>[],
  currentIndex: number = 0
) => {
  const item = get(array, currentIndex);
  if (typeof item === "function") {
    item().then(() => {
      const nextIndex = currentIndex + 1;
      const hasNextItem = get(array, nextIndex);
      if (hasNextItem) {
        asyncSubmit(array, nextIndex);
      }
    });
  }
};

export default {
  getBetSelectionsToShow,
  getBetSelectionSpecialGroup,
  extractArray,
  findWagerTypeByGroup,
  getMultiplierSpecialGroup,
  getBetDetails,
  asyncSubmit
};
